/**
 * HTTP ↔ Pinia casing boundary for checkout (`/api/checkout/*`).
 *
 * Extracted (not inline in the store) because **two** client modules touch the wire:
 * `useCheckoutService` serializes outbound bodies; `useShoppingSessionStore` deserializes inbound
 * responses. Same intent as `designProject.store` TRANSFORMATIONS — here the payloads are flat enough
 * for `changeKeys` instead of hand-mapped fields.
 *
 * @see {@link CheckoutContextInput} · {@link CheckoutContextPayload} in `types/checkout/client.ts`
 */
import * as changeKeys from 'change-case/keys'
import type {
    ApplyCouponClientResponse,
    ApplyCouponResponse,
    CheckForCreditsClientResponse,
    CheckForCreditsResponse,
    CheckoutAddressesData,
    CheckoutContextInput,
    CheckoutContextPayload,
    CheckoutEntitlementsData,
    CheckoutShippingData,
    CheckoutTotalsData,
    FinalizeCartClientResponse,
    FinalizeCartResponse,
    GetPaymentIntentClientResponse,
    GetPaymentIntentResponse,
    PlaceOrderClientResponse,
    PlaceOrderResponse,
    PrepareCartClientResponse,
    PrepareCartResponse,
    SyncReturnAddressClientResponse,
    SyncReturnAddressResponse,
} from '~~/layers/base/types/checkout'

/** Depth for nested finalize fields (`cart_totals`, `shipping_methods`) after key conversion. */
const CHECKOUT_API_KEY_DEPTH = 2

export function serializeCheckoutContext(input: CheckoutContextInput): CheckoutContextPayload {
    return changeKeys.snakeCase(input, 1) as CheckoutContextPayload
}

export function deserializePrepareCartResponse(
    raw: PrepareCartResponse
): PrepareCartClientResponse {
    // Address objects keep ACCS field names (`country_code`); only top-level keys camelCase.
    const { shipping_address: shippingAddress, billing_address: billingAddress, ...rest } = raw
    const base = changeKeys.camelCase(rest, 1) as PrepareCartClientResponse
    return {
        ...base,
        ...(shippingAddress !== undefined ? { shippingAddress } : {}),
        ...(billingAddress !== undefined ? { billingAddress } : {}),
    }
}

export function deserializeCheckForCreditsResponse(
    raw: CheckForCreditsResponse
): CheckForCreditsClientResponse {
    return changeKeys.camelCase(raw, CHECKOUT_API_KEY_DEPTH) as CheckForCreditsClientResponse
}

export function deserializeFinalizeCartResponse(
    raw: FinalizeCartResponse
): FinalizeCartClientResponse {
    return changeKeys.camelCase(raw, CHECKOUT_API_KEY_DEPTH) as FinalizeCartClientResponse
}

export function deserializePlaceOrderResponse(raw: PlaceOrderResponse): PlaceOrderClientResponse {
    return changeKeys.camelCase(raw, CHECKOUT_API_KEY_DEPTH) as PlaceOrderClientResponse
}

export function deserializeApplyCouponResponse(
    raw: ApplyCouponResponse
): ApplyCouponClientResponse {
    return changeKeys.camelCase(raw, CHECKOUT_API_KEY_DEPTH) as ApplyCouponClientResponse
}

export function deserializeSyncReturnAddressResponse(
    raw: SyncReturnAddressResponse
): SyncReturnAddressClientResponse {
    return changeKeys.camelCase(raw, CHECKOUT_API_KEY_DEPTH) as SyncReturnAddressClientResponse
}

export function deserializeGetPaymentIntentResponse(
    raw: GetPaymentIntentResponse
): GetPaymentIntentClientResponse {
    return changeKeys.camelCase(raw, CHECKOUT_API_KEY_DEPTH) as GetPaymentIntentClientResponse
}

export function entitlementsFromCheckForCredits(
    response: CheckForCreditsClientResponse | null | undefined
): CheckoutEntitlementsData {
    // Wire omits flags when false; coerce so segment booleans are always defined.
    const vouchersGranted = response?.stationeryVouchersGranted
    const vouchersAvailable = response?.stationeryVouchersAvailable
    const remaining = response?.noteCreditsRemaining
    const granted = response?.noteCreditsGranted
    const consumed = response?.noteCreditsConsumed
    return {
        hasNoteCredit: Boolean(response?.hasNoteCredit),
        hasStationeryVoucher: Boolean(response?.hasStationeryVoucher),
        isProUser: Boolean(response?.isProUser),
        stationeryVouchersGranted: typeof vouchersGranted === 'number' ? vouchersGranted : null,
        stationeryVouchersAvailable:
            typeof vouchersAvailable === 'number' ? vouchersAvailable : null,
        noteCreditsRemaining: typeof remaining === 'number' ? remaining : null,
        noteCreditsGranted: typeof granted === 'number' ? granted : null,
        noteCreditsConsumed: typeof consumed === 'number' ? consumed : null,
        proTier: response?.proTier ?? null,
        voucherUnavailableReason: response?.voucherUnavailableReason ?? null,
        subscriptionExpiresAt: response?.subscriptionExpiresAt ?? null,
    }
}

export function totalsFromFinalizeCart(
    response: FinalizeCartClientResponse | null | undefined
): CheckoutTotalsData {
    const raw = response?.cartTotals as Record<string, unknown> | undefined
    const grand = raw?.grandTotal
    const sub = raw?.subtotal
    const tax = raw?.taxAmount
    return {
        grandTotal: typeof grand === 'number' ? grand : null,
        subtotal: typeof sub === 'number' ? sub : null,
        taxAmount: typeof tax === 'number' ? tax : null,
        currency: typeof raw?.currency === 'string' ? raw.currency : null,
    }
}

export function shippingFromFinalizeCart(
    response: FinalizeCartClientResponse | null | undefined
): CheckoutShippingData {
    const methods = Array.isArray(response?.shippingMethods) ? response.shippingMethods : []
    return {
        methods,
        selectedMethodCode: methods[0]?.code ?? null,
    }
}

/**
 * First line-item SKU from finalize-cart (linked stationery `design_project.sku`).
 * Used as `place-cart` async_credit quantity-preflight `sku`.
 */
export function skuFromFinalizeCart(
    response: FinalizeCartClientResponse | null | undefined
): string | null {
    const items = response?.items
    if (!Array.isArray(items)) {
        return null
    }
    for (const item of items) {
        if (!item || typeof item !== 'object') {
            continue
        }
        const sku = (item as { sku?: unknown }).sku
        if (typeof sku === 'string' && sku.trim()) {
            return sku.trim()
        }
    }
    return null
}

export function addressesFromFinalizeCart(
    response: FinalizeCartClientResponse | null | undefined
): CheckoutAddressesData {
    return {
        shippingAddress: response?.shippingAddress ?? null,
        billingAddress: response?.billingAddress ?? null,
    }
}

/** Addresses from prepare-cart when signed-in DB resolution succeeds (before finalize). */
export function addressesFromPrepareCart(
    response:
        | Pick<PrepareCartClientResponse, 'shippingAddress' | 'billingAddress'>
        | null
        | undefined
): CheckoutAddressesData | null {
    if (!response) {
        return null
    }
    const shippingAddress = response.shippingAddress ?? null
    const billingAddress = response.billingAddress ?? null
    if (!shippingAddress && !billingAddress) {
        return null
    }
    return { shippingAddress, billingAddress }
}
